import { create } from 'zustand';

import { getView, type PrismMode } from '../../engine/prism.js';
import { useScene } from '../../state/scene.js';
import { collapseHistory } from './particle-model.js';

/**
 * Particle preview playback (Unity's "Particle Effect" controls), shared by
 * the Particle Editor panel and the Scene view overlay.
 *
 * The controls go to every live view that previews particles as
 * `setOption("particlePreview", json)` (prism_runtime
 * runtime/particle_preview.rs): the Scene view plays the selected system
 * (plus the editor's locked one), the editor's isolated `preview` view plays
 * its target. Each view reports back with `particlePreview` events.
 */

export interface PreviewSystemReport {
  id: string;
  count: number;
  playing: boolean;
  /** Seconds into the current cycle. */
  time: number;
  duration: number;
  looping: boolean;
}

export interface PreviewReport {
  /** Playback time since the last restart (seconds). */
  time: number;
  speed: number;
  paused: boolean;
  stopped: boolean;
  particles: number;
  systems: PreviewSystemReport[];
}

type PreviewMode = Extract<PrismMode, 'scene' | 'preview'>;
const MODES: readonly PreviewMode[] = ['scene', 'preview'];

export const PREVIEW_SPEEDS = [0.1, 0.25, 0.5, 1, 2, 4] as const;

interface ParticlePreviewState {
  paused: boolean;
  stopped: boolean;
  speed: number;
  /** Entity the Particle Editor is locked to (null = it follows the selection). */
  locked: string | null;
  reports: Partial<Record<PreviewMode, PreviewReport>>;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  restart: () => void;
  stop: () => void;
  /** Scrub to a playback time (pauses, like Unity). */
  seek: (time: number) => void;
  setSpeed: (speed: number) => void;
  setLocked: (id: string | null) => void;
  /** Re-sends the full state (a view that just started). */
  sync: (mode?: PreviewMode) => void;
  fromEngine: (mode: PrismMode, event: Record<string, unknown>) => void;
}

/** The entity the editor edits: the locked one, else the active selected entity with a particle system. */
export function particleTarget(locked = useParticlePreview.getState().locked): string | null {
  const { doc, selection } = useScene.getState();
  if (!doc) return null;
  const has = (id: string | null | undefined) =>
    Boolean(id && doc.entities.some((e) => e.id === id && e.components && 'particleSystem' in e.components));
  if (has(locked)) return locked;
  for (let i = selection.length - 1; i >= 0; i--) if (has(selection[i])) return selection[i]!;
  return null;
}

/** What one view is sent: the shared controls plus the entities it previews besides its selection. */
export function previewOption(mode: PreviewMode, state: { paused: boolean; speed: number; locked: string | null }, extra: Record<string, unknown> = {}) {
  const target = particleTarget(state.locked);
  const ids = mode === 'preview' ? (target ? [target] : []) : state.locked && target === state.locked ? [state.locked] : [];
  return { paused: state.paused, speed: state.speed, ids, ...extra };
}

export const useParticlePreview = create<ParticlePreviewState>((set, get) => {
  const send = (extra: Record<string, unknown> = {}, only?: PreviewMode) => {
    for (const mode of only ? [only] : MODES) {
      getView(mode)?.setOption('particlePreview', JSON.stringify(previewOption(mode, get(), extra)));
    }
  };
  return {
    paused: false,
    stopped: false,
    speed: 1,
    locked: null,
    reports: {},
    play: () => {
      set({ paused: false, stopped: false });
      send({ play: true });
    },
    pause: () => {
      set({ paused: true });
      send();
    },
    togglePlay: () => (get().paused || get().stopped ? get().play() : get().pause()),
    restart: () => {
      set({ stopped: false });
      send({ restart: true });
    },
    stop: () => {
      set({ stopped: true, paused: false });
      send({ stop: true });
    },
    seek: (time) => {
      set({ paused: true, stopped: false });
      send({ seek: Math.max(0, time) });
    },
    setSpeed: (speed) => {
      set({ speed });
      send();
    },
    setLocked: (locked) => {
      set({ locked });
      send();
    },
    sync: (mode) => send(get().stopped ? { stop: true } : {}, mode),
    fromEngine: (mode, event) => {
      if (mode !== 'scene' && mode !== 'preview') return;
      const report: PreviewReport = {
        time: Number(event.time) || 0,
        speed: Number(event.speed) || 0,
        paused: event.paused === true,
        stopped: event.stopped === true,
        particles: Number(event.particles) || 0,
        systems: Array.isArray(event.systems) ? (event.systems as PreviewSystemReport[]) : [],
      };
      set((s) => ({ reports: { ...s.reports, [mode]: report } }));
      // A view that started (or reloaded) after the controls changed catches up.
      const { paused, speed } = get();
      if (!report.stopped && (report.paused !== paused || Math.abs(report.speed - speed) > 1e-6)) send({}, mode);
    },
  };
});

/** The report to show: the editor's own preview view when it runs, else the Scene view's. */
export function activeReport(reports: Partial<Record<PreviewMode, PreviewReport>>): PreviewReport | null {
  return reports.preview ?? reports.scene ?? null;
}

/* ------------------------------------------------------------------ */
/* Undo coalescing: one undo step per drag in the particle editor.     */

let gestureStart: unknown = null;
let gestureOpen = false;

/** Call on pointer down: edits until {@link endEditGesture} become one undo step. */
export function beginEditGesture(): void {
  gestureOpen = true;
  gestureStart = useScene.getState().doc;
}

/** Call on pointer up: collapses the scene history steps of the gesture. */
export function endEditGesture(): void {
  if (!gestureOpen) return;
  gestureOpen = false;
  const start = gestureStart;
  gestureStart = null;
  const { past } = useScene.getState();
  const next = collapseHistory(past as readonly unknown[], start);
  if (next !== past) useScene.setState({ past: next as typeof past });
}
